import { api } from "~/utils/api"
import { asPoGranularity, type PoGranularity } from "~/utils/poGranularity"

import { trackEvent } from "./useTrackingService"
import { toast } from "react-toastify"

export const useUpdateCompanyMutation = () => {
  const utils = api.useUtils()

  return api.company.update.useMutation({
    onSuccess: () => {
      utils.invalidate()
      trackEvent("Company Updated")
      toast.success("Company settings updated")
    },
  })
}

export const useUpdateCompanyHakioSettings = () => {
  const utils = api.useUtils()

  return api.company.updateHakioSettings.useMutation({
    onSuccess: () => {
      utils.invalidate()
      trackEvent("Company Hakio settings Updated")
      toast.success("Company settings updated")
    },
  })
}

export const useUpdateForecastAlertThreshold = () => {
  const utils = api.useUtils()

  return api.company.updateForecastAlertThreshold.useMutation({
    onSuccess: () => {
      utils.invalidate()
      trackEvent("Company Forecast Alert Threshold Updated")
      toast.success("Alert threshold updated")
    },
    onError: (err) => {
      toast.error(`Failed to update alert threshold: ${err.message}`)
    },
  })
}

export const useUpdateDisabledColumnIds = () => {
  const utils = api.useUtils()

  return api.company.updateDisabledColumnIds.useMutation({
    onSuccess: () => {
      utils.company.getCompany.invalidate()
      trackEvent("Company Disabled Columns Updated")
      toast.success("Available columns updated")
    },
    onError: (err) => {
      toast.error(`Failed to update available columns: ${err.message}`)
    },
  })
}

export const useCompany = () => api.company.getCompany.useQuery()

/**
 * PO split granularity for a domain. `noos` also covers the ad-hoc PO dialog, which is
 * replenishment-flavoured and has no supply plan of its own to read a setting from.
 */
export const usePoGranularity = (domain: "noos" | "fashion"): PoGranularity => {
  const { data: company } = useCompany()
  return asPoGranularity(
    domain === "fashion" ? company?.poGranularityFashion : company?.poGranularityNoos,
  )
}

export const useUpdatePoGranularity = () => {
  const utils = api.useUtils()

  return api.company.updatePoGranularity.useMutation({
    onSuccess: () => {
      utils.company.getCompany.invalidate()
      toast.success("PO creation settings updated")
    },
    onError: (err) => {
      toast.error(`Failed to update PO creation settings: ${err.message}`)
    },
  })
}

export const useSetActivatedFromVersion2 = () => {
  const utils = api.useUtils()

  return api.company.setActivatedFromVersion2.useMutation({
    onSuccess: ({ activatedFromVersion2 }) => {
      utils.invalidate()
      toast.success(`activatedFrom V2 ${activatedFromVersion2 ? "enabled" : "disabled"}`)
    },
    onError: (err) => {
      toast.error(`Failed to update flag: ${err.message}`)
    },
  })
}

/** Returns true while company is loading (unknown) or when data access is on. */
export function hasHakiDataAccess(company: { aiHakiDataAccess: boolean } | undefined): boolean {
  return !company || company.aiHakiDataAccess
}

export const useAnnConfig = () => api.company.getAnnConfig.useQuery()

export const useResetAnnConfig = () => {
  const utils = api.useUtils()

  return api.company.resetAnnConfig.useMutation({
    onSuccess: () => {
      utils.company.getAnnConfig.invalidate()
      toast.success("Config reset to default")
    },
    onError: (err) => {
      toast.error(`Failed to reset config: ${err.message}`)
    },
  })
}

export const useUpdatePoExportConfig = () => {
  const utils = api.useUtils()

  return api.company.updatePoExportConfig.useMutation({
    onSuccess: () => {
      utils.company.getCompany.invalidate()
      toast.success("PO export settings updated")
    },
  })
}

export const useUpdateAnnConfig = () => {
  const utils = api.useUtils()

  return api.company.updateAnnConfig.useMutation({
    onSuccess: () => {
      utils.company.getAnnConfig.invalidate()
    },
    onError: (err) => {
      toast.error(`Failed to save config: ${err.message}`)
    },
  })
}
